<template>
  <div class="pp-tab-surface">
    <div
      v-if="hasContent"
      class="pp-devpane"
    >
      <div class="pp-dev-card">
        <div
          v-if="developerLogo"
          class="logo-img"
        >
          <img
            :src="developerLogo"
            :alt="developerTitle"
          />
        </div>
        <div
          v-else
          class="logo"
          aria-hidden="true"
        >
          {{ initials }}
        </div>
        <h3>{{ developerTitle }}</h3>
        <div
          v-if="developerSub"
          class="sub"
        >
          {{ developerSub }}
        </div>
        <template v-if="developerDescription">
          <div
            v-if="'html' in developerDescription"
            class="prose"
            v-html="developerDescription.html"
          ></div>
          <div
            v-else
            class="prose"
          >
            {{ developerDescription.text }}
          </div>
        </template>
        <div
          v-if="developerRows.length"
          class="rows"
        >
          <div
            v-for="(row, idx) in developerRows"
            :key="idx"
            class="r"
          >
            <span class="l">{{ row.label }}</span>
            <span class="v">{{ row.value }}</span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="empty"
    >
      <p>{{ t('property.v2Developer.empty') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { sanitizeHtml } from '~/utils/sanitizeHtml';
import type { PropertyDetailed } from '@/types/properties';
import { decodeHtmlEntities, fixDoubleEncodedHtmlEntities } from '@/helpers/propertyDescription';
import { getDeveloperDescriptionHtml, getDeveloperLogoUrl } from '@/helpers/developer';
import { pickLocalized, type LocalizedField } from '@/utils/i18n/localizedField';

const props = defineProps<{
  item: PropertyDetailed;
}>();

const { t, locale } = useI18n();

const developerLogo = computed(() => getDeveloperLogoUrl(props.item) || null);

const developerTitle = computed(() => {
  const dev = props.item?.developer as { title?: LocalizedField } | undefined;
  return pickLocalized(dev?.title, locale.value, t('property.developerTab.fallbackTitle'));
});

const initials = computed(() => {
  return developerTitle.value
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
});

const developerSub = computed(() => {
  const dev = props.item?.developer as { country?: { en?: string }; founded_at?: string } | undefined;
  if (!dev) return null;
  const parts: string[] = [];
  if (dev.country?.en) parts.push(dev.country.en);
  if (dev.founded_at) parts.push(t('property.v2Developer.foundedYear', { year: dev.founded_at }));
  return parts.length ? parts.join(' · ') : null;
});

// Stored developer descriptions are untrusted: HTML is sanitized (client-only — DOMPurify needs a
// DOM; SSR and the first client render use stripped text) and plain text renders as text, never as HTML.
const isMounted = ref(false);
onMounted(() => {
  isMounted.value = true;
});
const developerDescription = computed<{ html: string } | { text: string } | null>(() => {
  const raw = getDeveloperDescriptionHtml(props.item.developer);
  if (!raw) return null;
  if (/<[a-z][\s\S]*>/i.test(raw)) {
    // Before mount (SSR + hydration) show the tag-stripped text so the content stays in the
    // server HTML for SEO; the sanitized HTML replaces it once mounted.
    if (!isMounted.value)
      return {
        text: decodeHtmlEntities(
          raw
            .replace(/<[^>]*>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim(),
        ),
      };
    return { html: sanitizeHtml(fixDoubleEncodedHtmlEntities(raw)) };
  }
  return { text: decodeHtmlEntities(raw) };
});

type DevRow = { label: string; value: string };

const developerRows = computed<DevRow[]>(() => {
  const dev = props.item?.developer as
    | {
        founded_at?: string;
        country?: { en?: string };
        delivered_units?: number | string;
        delivered_projects?: number | string;
      }
    | undefined;
  if (!dev) return [];
  const out: DevRow[] = [];
  if (dev.founded_at) out.push({ label: t('property.v2Developer.rows.founded'), value: String(dev.founded_at) });
  if (dev.country?.en) out.push({ label: t('property.v2Developer.rows.headquarters'), value: dev.country.en });
  if (dev.delivered_projects != null)
    out.push({ label: t('property.v2Developer.rows.projectsDelivered'), value: String(dev.delivered_projects) });
  if (dev.delivered_units != null)
    out.push({ label: t('property.v2Developer.rows.unitsDelivered'), value: String(dev.delivered_units) });
  return out;
});

const hasContent = computed(() => !!(developerLogo.value || developerDescription.value || developerRows.value.length));
</script>

<style scoped lang="scss">
.pp-tab-surface {
  background: #f8f7f3;
  border-radius: 20px;
  padding: 28px;
}

.pp-devpane {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
}

.pp-dev-card {
  background: white;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 16px;
  padding: 22px;

  .logo {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: linear-gradient(135deg, #8b5cf6, #6b4bcf);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Syne', 'Manrope', sans-serif;
    font-weight: 700;
    font-size: 18px;
    letter-spacing: 0.04em;
    margin-bottom: 14px;
  }

  .logo-img {
    width: 80px;
    height: 80px;
    border-radius: 14px;
    background: #f8f7f3;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
    padding: 8px;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  }

  h3 {
    font-family: 'Fraunces', 'Times New Roman', serif;
    font-weight: 600;
    font-size: 22px;
    letter-spacing: -0.015em;
    margin: 0 0 6px;
  }

  .sub {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10px;
    letter-spacing: 0.08em;
    color: rgba(13, 15, 21, 0.62);
    font-weight: 600;
    text-transform: uppercase;
    margin-bottom: 14px;
  }

  .prose {
    font-size: 13.5px;
    color: rgba(13, 15, 21, 0.7);
    line-height: 1.55;
    margin-bottom: 12px;

    :deep(p) {
      margin-bottom: 0.75em;
    }

    :deep(p:last-child) {
      margin-bottom: 0;
    }
  }

  .rows {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid rgba(13, 15, 21, 0.08);

    .r {
      display: flex;
      justify-content: space-between;
      font-size: 13px;

      .l {
        color: rgba(13, 15, 21, 0.62);
      }

      .v {
        color: #0d0f15;
        font-weight: 700;
      }
    }
  }
}

.empty {
  background: white;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 14px;
  padding: 40px;
  text-align: center;
  color: rgba(13, 15, 21, 0.62);
  font-size: 14px;
}
</style>
